import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { User, Smile, MessageCircle, Sparkles, Shield, Heart, CheckCircle, XCircle, Target, Activity, Camera } from 'lucide-react';
import { usePersonaConfig } from '../../hooks/usePersonaConfig';
import { useProdutoConfig } from '../../hooks/useProdutoConfig';
import { useEmojisConfig } from '../../hooks/useEmojisConfig';
import { useCurrentUserId } from '../../hooks/useCurrentUserId';
import { toast } from 'react-hot-toast';

const Container = styled(motion.div)`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

const AvatarWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto ${({ theme }) => theme.spacing[3]};
  position: relative;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const Avatar = styled.div<{ $hasImage?: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${({ $hasImage, theme }) => $hasImage ? 'transparent' : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryHover} 100%)`};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.neon};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  svg {
    width: 50%;
    height: 50%;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 30px ${({ theme }) => theme.colors.primary}80;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const AvatarOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 50%;

  ${Avatar}:hover & {
    opacity: 1;
  }

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

const AvatarHint = styled.span`
  font-size: 10px;
  color: white;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const StatusBadge = styled.div`
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background: #10b981;
  border: 3px solid ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 50%;
  box-shadow: 0 0 10px #10b98180;
`;

const BotName = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const BotRole = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing[3]};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: fit-content;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const CardIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const CardContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  flex: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  }
`;

const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const FullWidthSection = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const IconWrapper = styled.div<{ $color?: string; $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${props => props.$active ? (props.$color || props.theme.colors.success) : props.theme.colors.error};
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const EmojiPreview = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing[4]};
`;

export const IdentidadePessoal: React.FC = () => {
  const { userId } = useCurrentUserId();
  const { persona, isLoading: personaLoading, savePersona, isSaving } = usePersonaConfig(userId);
  const { produto, isLoading: produtoLoading } = useProdutoConfig(userId);
  const { emojis, isLoading: emojisLoading } = useEmojisConfig(userId);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isLoading = personaLoading || produtoLoading || emojisLoading;

  // Handler para upload de avatar
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }

    // Validar tamanho (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 2MB');
      return;
    }

    // Converter para base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      savePersona({ ...persona, avatarUrl: base64 });
    };
    reader.onerror = () => {
      toast.error('Erro ao processar imagem');
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarClick = () => {
    if (!isSaving) {
      fileInputRef.current?.click();
    }
  };

  if (isLoading) {
    return (
      <LoadingState>
        <User size={48} style={{ opacity: 0.5 }} />
        <p>Carregando identidade do bot...</p>
      </LoadingState>
    );
  }

  const getFormalidadeDescricao = (nivel: number) => {
    if (nivel <= 3) return 'Informal e próximo';
    if (nivel <= 6) return 'Equilibrado';
    return 'Formal e profissional';
  };

  const getGeneroEmoji = (genero: string) => {
    switch (genero) {
      case 'feminino': return '👩';
      case 'masculino': return '👨';
      default: return '🤖';
    }
  };

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Header>
        <AvatarWrapper>
          <Avatar $hasImage={!!persona.avatarUrl} onClick={handleAvatarClick}>
            {persona.avatarUrl ? (
              <AvatarImage src={persona.avatarUrl} alt={persona.nomeBot} />
            ) : (
              <User color="white" />
            )}
            <AvatarOverlay>
              <Camera />
              <AvatarHint>{isSaving ? 'Salvando...' : 'Alterar'}</AvatarHint>
            </AvatarOverlay>
          </Avatar>
          <StatusBadge />
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
          />
        </AvatarWrapper>
        <BotName>{persona.nomeBot} {getGeneroEmoji(persona.genero)}</BotName>
        <BotRole>Assistente Virtual de Atendimento</BotRole>
      </Header>

      <Grid>
        {/* Personalidade */}
        <Card
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <CardHeader>
            <CardIcon $color="#8b5cf6">
              <Sparkles size={24} />
            </CardIcon>
            <CardTitle>Personalidade</CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow>
              <InfoLabel>Gênero</InfoLabel>
              <InfoValue style={{ textTransform: 'capitalize' }}>{persona.genero}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Tom</InfoLabel>
              <InfoValue style={{ textTransform: 'capitalize' }}>{persona.tom}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Formalidade</InfoLabel>
              <InfoValue>
                {persona.nivelFormalidade}/10 - {getFormalidadeDescricao(persona.nivelFormalidade)}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Humor</InfoLabel>
              <InfoValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconWrapper $active={persona.usarHumor}>
                  {persona.usarHumor ? <CheckCircle size={18} /> : <XCircle size={18} />}
                </IconWrapper>
                {persona.usarHumor ? 'Ativado' : 'Desativado'}
              </InfoValue>
            </InfoRow>
          </CardContent>
        </Card>

        {/* Linguagem */}
        <Card
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <CardHeader>
            <CardIcon $color="#10b981">
              <MessageCircle size={24} />
            </CardIcon>
            <CardTitle>Linguagem</CardTitle>
          </CardHeader>
          <CardContent>
            <FullWidthSection>
              <InfoLabel>Gírias Permitidas ({persona.giriasPermitidas?.length || 0})</InfoLabel>
              <TagContainer>
                {persona.giriasPermitidas?.slice(0, 8).map((giria, index) => (
                  <Tag key={index}>{giria}</Tag>
                ))}
                {(persona.giriasPermitidas?.length || 0) > 8 && (
                  <Tag>+{(persona.giriasPermitidas?.length || 0) - 8}</Tag>
                )}
              </TagContainer>
            </FullWidthSection>

            <FullWidthSection>
              <InfoLabel>Abreviações ({persona.abreviacoesPermitidas?.length || 0})</InfoLabel>
              <TagContainer>
                {persona.abreviacoesPermitidas?.slice(0, 8).map((abrev, index) => (
                  <Tag key={index}>{abrev}</Tag>
                ))}
                {(persona.abreviacoesPermitidas?.length || 0) > 8 && (
                  <Tag>+{(persona.abreviacoesPermitidas?.length || 0) - 8}</Tag>
                )}
              </TagContainer>
            </FullWidthSection>
          </CardContent>
        </Card>

        {/* Emojis */}
        <Card
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <CardHeader>
            <CardIcon $color="#f472b6">
              <Smile size={24} />
            </CardIcon>
            <CardTitle>Emojis</CardTitle>
          </CardHeader>
          <CardContent>
            <FullWidthSection>
              <InfoLabel>Positivos</InfoLabel>
              <EmojiPreview>
                {emojis.positivos?.slice(0, 10).map((emoji, index) => (
                  <span key={index}>{emoji}</span>
                ))}
              </EmojiPreview>
            </FullWidthSection>

            <div>
              <InfoLabel>Saudação</InfoLabel>
              <EmojiPreview>
                {emojis.saudacao?.map((emoji, index) => (
                  <span key={index}>{emoji}</span>
                ))}
              </EmojiPreview>
            </div>

            <div>
              <InfoLabel>Fechamento</InfoLabel>
              <EmojiPreview>
                {emojis.fechamento?.map((emoji, index) => (
                  <span key={index}>{emoji}</span>
                ))}
              </EmojiPreview>
            </div>
          </CardContent>
        </Card>

        {/* Produto Foco */}
        <Card
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <CardHeader>
            <CardIcon $color="#f59e0b">
              <Shield size={24} />
            </CardIcon>
            <CardTitle>Produto Foco</CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow>
              <InfoLabel>Produto</InfoLabel>
              <InfoValue>{produto.nome}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Tipo</InfoLabel>
              <InfoValue style={{ textTransform: 'capitalize' }}>{produto.tipo}</InfoValue>
            </InfoRow>
            <FullWidthSection style={{ marginTop: '8px' }}>
              <InfoLabel>Foco Principal</InfoLabel>
              <InfoValue style={{
                lineHeight: '1.6',
                fontSize: '0.875rem',
                fontWeight: 'normal'
              }}>
                {produto.foco}
              </InfoValue>
            </FullWidthSection>
          </CardContent>
        </Card>

        {/* Restrições */}
        <Card
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <CardHeader>
            <CardIcon $color="#ef4444">
              <Heart size={24} />
            </CardIcon>
            <CardTitle>Diretrizes</CardTitle>
          </CardHeader>
          <CardContent>
            <FullWidthSection>
              <InfoLabel>Escopo de Atendimento</InfoLabel>
              <InfoValue style={{
                lineHeight: '1.6',
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 'normal'
              }}>
                {produto.escopo}
              </InfoValue>
            </FullWidthSection>

            {produto.palavrasProibidas && produto.palavrasProibidas.length > 0 && (
              <FullWidthSection>
                <InfoLabel>Tópicos Proibidos ({produto.palavrasProibidas.length})</InfoLabel>
                <TagContainer>
                  {produto.palavrasProibidas.slice(0, 8).map((palavra, index) => (
                    <Tag key={index} style={{ background: '#ef444420', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <XCircle size={12} />
                      {palavra}
                    </Tag>
                  ))}
                  {produto.palavrasProibidas.length > 8 && (
                    <Tag style={{ background: '#ef444420', color: '#ef4444' }}>
                      +{produto.palavrasProibidas.length - 8}
                    </Tag>
                  )}
                </TagContainer>
              </FullWidthSection>
            )}
          </CardContent>
        </Card>

        {/* Estratégia de Atendimento */}
        <Card
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <CardHeader>
            <CardIcon $color="#3b82f6">
              <Target size={24} />
            </CardIcon>
            <CardTitle>Estratégia</CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow>
              <InfoLabel>Link de Compra</InfoLabel>
              <InfoValue style={{ 
                fontSize: '0.75rem',
                wordBreak: 'break-all',
                color: produto.linkCompra ? '#3b82f6' : '#6b7280'
              }}>
                {produto.linkCompra || 'Não configurado'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>System Prompt</InfoLabel>
              <InfoValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconWrapper $active={!!persona.templateSystemPrompt}>
                  {persona.templateSystemPrompt ? <CheckCircle size={18} /> : <XCircle size={18} />}
                </IconWrapper>
                {persona.templateSystemPrompt ? 'Customizado' : 'Padrão'}
              </InfoValue>
            </InfoRow>
            <FullWidthSection style={{ marginTop: '8px' }}>
              <InfoLabel>Configurações Ativas</InfoLabel>
              <TagContainer>
                {persona.giriasPermitidas?.length > 0 && (
                  <Tag style={{ background: '#10b98120', color: '#10b981' }}>
                    <Activity size={12} style={{ marginRight: '4px' }} />
                    Gírias
                  </Tag>
                )}
                {persona.abreviacoesPermitidas?.length > 0 && (
                  <Tag style={{ background: '#10b98120', color: '#10b981' }}>
                    <Activity size={12} style={{ marginRight: '4px' }} />
                    Abreviações
                  </Tag>
                )}
                {emojis.positivos?.length > 0 && (
                  <Tag style={{ background: '#f472b620', color: '#f472b6' }}>
                    <Smile size={12} style={{ marginRight: '4px' }} />
                    Emojis
                  </Tag>
                )}
                {produto.palavrasProibidas?.length > 0 && (
                  <Tag style={{ background: '#ef444420', color: '#ef4444' }}>
                    <Shield size={12} style={{ marginRight: '4px' }} />
                    Restrições
                  </Tag>
                )}
              </TagContainer>
            </FullWidthSection>
          </CardContent>
        </Card>
      </Grid>
    </Container>
  );
};
